import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Car } from "lucide-react";

export default function VehicleDetail() {
  const { car_number } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/vehicles/${encodeURIComponent(car_number)}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Not found"));
  }, [car_number]);

  if (err) return (
    <div className="p-8 space-y-4">
      <Link to="/vehicles" className="text-xs text-zinc-500 hover:text-amber-500">← back to vehicles</Link>
      <div className="text-zinc-400">{err}</div>
    </div>
  );
  if (!data) return <div className="p-8 font-mono-data"><span className="ascii-load" /></div>;

  return (
    <div className="p-8 space-y-6" data-testid="vehicle-detail-page">
      <Link to="/vehicles" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-amber-500">
        <ArrowLeft className="w-3.5 h-3.5" /> all vehicles
      </Link>

      <header>
        <div className="overline">// digital service file</div>
        <div className="flex items-center gap-3 mt-1">
          <Car className="w-7 h-7 text-amber-500" />
          <h1 className="font-display font-black text-3xl tracking-wider text-amber-300">{data.car_number}</h1>
        </div>
        <div className="text-zinc-500 text-sm mt-1 font-mono-data">
          {data.query_count} {data.query_count === 1 ? "query" : "queries"} ·
          {data.vehicles.length > 0 && ` ${data.vehicles.join(", ")} ·`}
          {data.mechanics_touched.length} mechanic{data.mechanics_touched.length !== 1 ? "s" : ""}
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="gs-card p-4">
          <div className="overline">Total Queries</div>
          <div className="font-mono-data font-bold text-3xl mt-2 text-amber-500">{data.query_count}</div>
        </div>
        <div className="gs-card p-4">
          <div className="overline">Mechanics</div>
          <div className="font-mono-data font-bold text-3xl mt-2">{data.mechanics_touched.length}</div>
        </div>
        <div className="gs-card p-4">
          <div className="overline">Unique DTCs</div>
          <div className="font-mono-data font-bold text-3xl mt-2">
            {new Set(data.diagnostics.filter((d) => d.dtc_code).map((d) => d.dtc_code)).size}
          </div>
        </div>
        <div className="gs-card p-4">
          <div className="overline">Avg Confidence</div>
          <div className="font-mono-data font-bold text-3xl mt-2 text-blue-400">
            {Math.round(data.diagnostics.reduce((a, d) => a + (d.confidence || 0), 0) / data.diagnostics.length)}%
          </div>
        </div>
      </div>

      <div>
        <div className="overline mb-3">Diagnostic Timeline (newest first)</div>
        <div className="gs-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-900 overline">
              <tr className="text-left">
                <th className="px-4 py-2.5">When</th>
                <th className="px-4 py-2.5">Mechanic</th>
                <th className="px-4 py-2.5">Query</th>
                <th className="px-4 py-2.5">DTC</th>
                <th className="px-4 py-2.5 text-right">Conf</th>
              </tr>
            </thead>
            <tbody>
              {data.diagnostics.map((d) => (
                <tr key={d.id} className="border-b border-zinc-900 align-top hover:bg-zinc-900/40" data-testid={`vehicle-diag-${d.id}`}>
                  <td className="px-4 py-3 font-mono-data text-xs text-zinc-500 whitespace-nowrap">
                    {new Date(d.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/mechanics/${d.mechanic_id}`} className="text-zinc-200 hover:text-amber-500 text-sm">{d.mechanic_name}</Link>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-zinc-200 text-sm whitespace-pre-line">{d.query_text}</div>
                    <div className="text-xs text-zinc-500 mt-1 whitespace-pre-line">{d.ai_response}</div>
                  </td>
                  <td className="px-4 py-3">{d.dtc_code ? <span className="dtc-badge">{d.dtc_code}</span> : <span className="text-zinc-700">—</span>}</td>
                  <td className="px-4 py-3 text-right"><span className="confidence-badge">{d.confidence}%</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
